import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import { SAVANNA_DEVICE_NAME, SOFIA2_DEVICE_NAME } from 'src/app/services/labpartner-constants';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import { Experiment, KibanaData, UserAccess } from 'src/app/services/labpartner.service.model';
import { EVENT_RESULTS_LINK_FUSION } from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { BaseComponent } from 'src/app/support/base.component';

@Component({
  selector: 'app-results-links',
  templateUrl: './results-links.component.html',
  styleUrls: ['./results-links.component.scss'],
  standalone: false,
})
export class ResultsLinksComponent extends BaseComponent implements OnChanges {
  @Input() experiment: Experiment = {} as Experiment;
  @Input() showSavannaResultsLink: boolean = false;

  experimentCount: number = 0;
  fusionUrl: string = '';
  isSavannaDevice: boolean = false;
  isSofia2Device: boolean = false;
  refreshDelayMilliSecs: number = 0;
  currentUser: UserAccess | null = null;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private apiService: LabpartnerService,
    private experimentService: ExperimentService,
    private loggingService: LoggingService,
    private userAccountService: UserAccountService,
  ) {
    super();
  }
  ngOnChanges(_changes: SimpleChanges): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    if (!this.experiment) {
      return;
    }

    this.experimentCount = 0;
    this.fusionUrl = '';
    this.isSavannaDevice = false;
    this.isSofia2Device = false;
    this.subscription.add(
      this.userAccountService.currentUser$.pipe(takeUntil(this._destroying$)).subscribe(user => {
        this.currentUser = user as UserAccess;
        this.initializeDevicesAndFusionData();
      }),
    );
  }

  protected ngOnDestroyInternal(): void {}

  // This method initializes the devices and Fusion data for the experiment.
  // It checks if the experiment is a Savanna device and if the user has Fusion access.
  // If so, it sets the Fusion data and starts refreshing it at a specified interval.
  initializeDevicesAndFusionData() {
    if (!this.experiment || !this.currentUser || !this.currentUser.hasFusionAccess) {
      return;
    }

    let savannaDevice = this.experimentService.devices.find(x => x.value === SAVANNA_DEVICE_NAME);
    let sofia2Device = this.experimentService.devices.find(x => x.value === SOFIA2_DEVICE_NAME);

    if (
      (this.experiment.deviceType === savannaDevice?.id ||
        this.experiment.deviceType === sofia2Device?.id) &&
      this.currentUser?.hasFusionAccess
    ) {
      this.isSavannaDevice = this.experiment.deviceType === savannaDevice?.id;
      this.isSofia2Device = this.experiment.deviceType === sofia2Device?.id;

      // Determine the correct deviceTypeCode based on the experiment's device type
      // Use the typeCode from the device configuration (Savanna: 'WC', Sofia2: 'SL')
      const deviceTypeCode = this.isSavannaDevice
        ? savannaDevice?.typeCode || ''
        : this.isSofia2Device
          ? sofia2Device?.typeCode || ''
          : '';
      this.setFusionData(deviceTypeCode);
    }
  }

  // This method is called to set the Fusion data for the experiment.
  // It fetches the experiment count and the URL template from the API service.
  // It also sets the refresh delay in milliseconds and starts refreshing the Fusion data at a specified interval.
  setFusionData(deviceTypeCode: string) {
    if (!this.experiment) {
      return;
    }
    window.open(this.fusionUrl, '_blank');

    this.subscription.add(
      this.apiService
        .getExperimentCountForExperimentId(this.experiment?.experimentId, deviceTypeCode, true)
        .pipe(takeUntil(this._destroying$))
        .subscribe(
          data => {
            this.refreshDelayMilliSecs = data.delayToRefreshSecs * 1000;
            this.experimentCount = data.experimentCount;
            this.fusionUrl = data.urlTemplate;
            this.updateFusionData(data);
            this.refreshFusionData(deviceTypeCode);
          },
          error => {
            console.log(error);
          },
        ),
    );
  }

  // This method is called to refresh the Fusion data at a specified interval.
  // It uses the RxJS timer operator to create a timer that emits values at the specified interval.
  // It then fetches the experiment count for the experiment ID and device type code from the API service.
  refreshFusionData(deviceTypeCode: string) {
    if (this.experiment) {
      this.subscription.add(
        timer(this.refreshDelayMilliSecs, this.refreshDelayMilliSecs)
          .pipe(
            takeUntil(this._destroying$),
            switchMap(_ =>
              this.apiService.getExperimentCountForExperimentId(
                this.experiment.experimentId,
                deviceTypeCode,
                true,
              ),
            ),
          )
          .subscribe(data => {
            this.updateFusionData(data);
          }),
      );
    }
  }

  // This method updates the experiment count and the disabled state of the Fusion counter link.
  // If the experiment count is greater than 0, the link is enabled; otherwise, it is disabled.
  updateFusionData(data: KibanaData): void {
    this.experimentCount = data.experimentCount;
  }

  // This method is called when the user clicks on the Fusion counter link.
  // It opens the Fusion URL in a new tab and logs the event to the logging service.
  // Prevents the action if the link is disabled (experimentCount <= 0).
  onClickFusionCounterLink(event: Event) {
    // Prevent click if link is disabled
    if (this.experimentCount <= 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.experiment) {
      return;
    }
    window.open(this.fusionUrl, '_blank');

    const props: { [key: string]: number | string } = {
      ExperimentId: this.experiment.experimentId,
      DeviceType: this.experiment.deviceType,
      ExperimentOwner: this.experiment?.createdBy ?? 0,
      NumberOfResults: this.experimentCount,
    };

    this.loggingService.logEvent(EVENT_RESULTS_LINK_FUSION, props);
  }
}
